import React from "react";
import {Toast, ToastBody, ToastHeader} from "reactstrap";
import {IconSource} from "../../../shared/enums/iconSource";
import "./toastMessage.sass";
import Icon from "../common/icon/icon";
import {useTranslation} from "react-i18next";

export enum ToastMessageStatus {
  Success = "Success",
  Virtual = "Virtual",
  Danger = "Danger"
}

export interface IToastMessageProps {
  message: string;
  status: ToastMessageStatus;
  isOpen: boolean;
  header?: string;
  onClick: () => void;
}

/**
 * Generic component to display toast messages.
 * @param message - message to be displayed.
 * @param status - success, warning, or danger(error).
 * @param isOpen - true if the toast message is displayed.
 * @param onClick - click handler.
 * @param header - header must be there when toast message type is virtual.
 * @constructor
 */
const ToastMessage: React.FC<IToastMessageProps> = ({
  message,
  status,
  isOpen,
  header,
  onClick
}) => {
  const {t} = useTranslation(["translation", "toastMessage"]);

  const toastMessageStatusDict: {
    [key: string]: {
      className: string;
      header: string;
      icon: IconSource;
    };
  } = {
    [ToastMessageStatus.Success]: {
      className: "toast-success",
      header: t("toastMessage:title.success"),
      icon: IconSource.success
    },
    [ToastMessageStatus.Virtual]: {
      className: "toast-virtual",
      header: "",
      icon: IconSource.notification
    },
    [ToastMessageStatus.Danger]: {
      className: "toast-error",
      header: t("toastMessage:title.error"),
      icon: IconSource.error
    }
  };

  return isOpen ? (
    <div
      data-testid={"toast-message"}
      className={toastMessageStatusDict[status].className}
      onClick={onClick}
    >
      <Toast isOpen={isOpen}>
        <ToastHeader toggle={onClick}>
          <span>
            <Icon source={toastMessageStatusDict[status].icon} />
            {header ? header : toastMessageStatusDict[status].header}
          </span>
          <div>
            <Icon source={IconSource.exit}></Icon>
          </div>
        </ToastHeader>
        <ToastBody>{message}</ToastBody>
      </Toast>
    </div>
  ) : (
    <></>
  );
};

export default ToastMessage;
